import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormShell, FieldRow, ChipGroup, submitForm, SubmitButton, DoneScreen } from "./FormShell";
import DateInput from "./DateInput";
import { ICON_MAP } from "@/pages/MenuEditor";
import { ForkKnife } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const p = (k, d = "") => new URLSearchParams(window.location.search).get(k) || d;
const todayIso = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
const isoPlusDays = (days) => {
  const t = new Date();
  t.setDate(t.getDate() + days);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
const inp = "h-11 rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200";

export default function PublicMeal() {
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [d, setD] = useState({
    vorname: p("vorname"), nachname: p("nachname"),
    personen: p("guests", "1"), zimmer: p("room", ""),
    tag: todayIso(), uhrzeit: "18:00",
    unvertraeglichkeiten: "", bemerkungen: "",
  });
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/api/public/menu-catalog`).then(r => {
      const m = r.data?.meal || { subtitle: "", categories: [] };
      setCatalog(m);
      setD(prev => {
        const next = { ...prev };
        (m.categories || []).forEach(c => { if (!(c.key in next)) next[c.key] = []; });
        return next;
      });
    }).catch(() => setCatalog({ subtitle: "", categories: [] }));
  }, []);

  // Auswahl-Sperre: sobald irgendein Gericht gewählt ist, sperren wir alle anderen.
  const totalSelected = (catalog?.categories || []).reduce(
    (sum, c) => sum + ((d[c.key] || []).length), 0
  );
  const isLocked = totalSelected >= 1;

  const submit = (e) => {
    e.preventDefault();
    if (!d.tag || d.tag < todayIso() || d.tag > isoPlusDays(365)) {
      alert("Bitte ein gültiges Datum zwischen heute und in einem Jahr wählen."); return;
    }
    if (totalSelected === 0) {
      alert("Bitte ein Hauptgericht auswählen."); return;
    }
    submitForm({
      form_type: "dinner", data: d,
      guest_name: `${d.vorname} ${d.nachname}`.trim(), room_hint: d.zimmer,
      setSaving, onDone: () => setDone(true),
    });
  };

  if (done) return <DoneScreen label="Ihre Bestellung ist eingegangen – wir servieren zur gewünschten Uhrzeit." />;
  if (!catalog) return <FormShell title="Mittag- & Abendessen"><div className="text-sm text-slate-500">Lade Speisekarte…</div></FormShell>;

  return (
    <FormShell title="Mittag- & Abendessen" subtitle={catalog.subtitle}>
      <form onSubmit={submit}>
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 leading-relaxed" data-testid="meal-notice">
          <span className="font-semibold">💡 Wichtiger Hinweis:</span> Damit wir Ihre Speisen frisch und in bester Qualität zubereiten können, bitten wir Sie, <b>pro Zimmer ein gemeinsames Gericht</b> für alle Personen auszuwählen. Vielen Dank für Ihr Verständnis. 😊🏡🍽️
        </div>

        <FieldRow label="Vorname" required>
          <Input value={d.vorname} onChange={(e) => set("vorname", e.target.value)} required className={inp} data-testid="meal-firstname" />
        </FieldRow>
        <FieldRow label="Nachname" required>
          <Input value={d.nachname} onChange={(e) => set("nachname", e.target.value)} required className={inp} data-testid="meal-lastname" />
        </FieldRow>
        <FieldRow label="Personen">
          <Input type="number" min="1" value={d.personen} onChange={(e) => set("personen", e.target.value)} className={inp} data-testid="meal-personen" />
        </FieldRow>
        <FieldRow label="Zimmer">
          <Input value={d.zimmer} onChange={(e) => set("zimmer", e.target.value)} placeholder="z. B. Apartment DG (optional)" className={inp} data-testid="meal-zimmer" />
        </FieldRow>
        <FieldRow label="Tag" required>
          <DateInput value={d.tag} onChange={(v) => set("tag", v)} required min="today" max={isoPlusDays(365)} testid="meal-tag" />
        </FieldRow>
        <FieldRow label="Uhrzeit" required>
          <Input type="time" value={d.uhrzeit} onChange={(e) => set("uhrzeit", e.target.value)} required className={inp} data-testid="meal-uhrzeit" />
        </FieldRow>

        <div className="mt-8 pt-6 border-t border-slate-100 space-y-8">
          {(catalog.categories || []).map((cat) => (
            <ChipGroup
              key={cat.key}
              icon={ICON_MAP[cat.icon] || ForkKnife}
              label={cat.label}
              locked={isLocked}
              options={(cat.items || []).map(i => i.name)}
              prices={Object.fromEntries((cat.items || []).map(i => [i.name, i.price]))}
              value={d[cat.key] || []}
              onChange={(v) => set(cat.key, v)}
              testid={`meal-${cat.key}`}
            />
          ))}
          {isLocked && (
            <div className="pl-0 md:pl-10 text-xs text-slate-500 italic" data-testid="meal-lock-hint">
              Sie haben ein Gericht gewählt. Klicken Sie es erneut an, um Ihre Wahl zu ändern.
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100">
          <FieldRow label="Besondere Wünsche">
            <Textarea value={d.unvertraeglichkeiten} onChange={(e) => set("unvertraeglichkeiten", e.target.value)}
                      className="rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200" rows={2} data-testid="meal-unvert" />
          </FieldRow>
          <FieldRow label="Bemerkungen">
            <Textarea value={d.bemerkungen} onChange={(e) => set("bemerkungen", e.target.value)}
                      className="rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200" rows={2} data-testid="meal-bemerkungen" />
          </FieldRow>
        </div>

        <SubmitButton saving={saving} label="Bestellung absenden" />
      </form>
    </FormShell>
  );
}
